import React, { forwardRef } from "react";

import { cn } from "@/lib/utils";

export type ToggleButtonProps = {
  readonly value?: boolean;
  readonly onChange?: (value: boolean) => void;
  readonly disabled?: boolean;
  readonly "aria-invalid"?: boolean;
  readonly "aria-describedby"?: string;
  readonly id?: string;
  readonly trueLabel?: string;
  readonly falseLabel?: string;
  readonly className?: string;
  readonly variant?: "default" | "v3";
};

const ToggleButton = forwardRef<HTMLDivElement, ToggleButtonProps>(
  (
    {
      value,
      onChange,
      disabled = false,
      id,
      trueLabel = "Yes",
      falseLabel = "No",
      className,
      variant = "default",
    },
    ref
  ) => {
    const getButtonClasses = (isSelected: boolean) => {
      const baseClasses =
        variant === "v3"
          ? "h-[30px] w-full rounded-full border px-6 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-color/20 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-2"
          : "h-[30px] rounded-full border px-6 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#068762] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

      if (isSelected) {
        const selectedClasses =
          variant === "v3"
            ? "border-gray-300 bg-brand-color/20 text-primary shadow-sm"
            : "border-[#068762] bg-[#068762] text-white shadow-sm";
        return cn(baseClasses, selectedClasses);
      } else {
        return cn(
          baseClasses,
          "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        );
      }
    };

    return (
      <div
        aria-label="Toggle selection"
        className={cn("flex gap-2", className, variant === "v3" && "w-full")}
        id={id}
        ref={ref}
        role="group"
      >
        <button
          aria-pressed={value === true}
          className={getButtonClasses(value === true)}
          disabled={disabled}
          type="button"
          onClick={() => {
            if (!disabled && onChange) {
              onChange(true);
            }
          }}
        >
          {trueLabel}
        </button>

        <button
          aria-pressed={value === false}
          className={getButtonClasses(value === false)}
          disabled={disabled}
          type="button"
          onClick={() => {
            if (!disabled && onChange) {
              onChange(false);
            }
          }}
        >
          {falseLabel}
        </button>
      </div>
    );
  }
);

ToggleButton.displayName = "ToggleButton";

export default ToggleButton;
